import {
  CreditCard,
  Currency,
  CustomParameters,
  Duration,
  DurationUnit,
  PaywallApi,
  PaywallFeature,
  PaywallSession,
  Plan,
  PurchaseOptions,
  StoredCreditCard,
  Subscription,
  SubscriptionStatus,
  Voucher,
} from '@accedo/one-plugin-sdk';
import { t } from 'i18next';
import { useLocation } from 'react-router-dom';

import {
  axios,
  FormInputTypes,
  mppService,
  TFormData,
  userService,
} from '#/client';
import { SELECTED_PLAN_KEY } from '#/components/Form/Form';
import { InputPlan } from '#/components/Form/Form.types';
import { useConfig } from '#/hooks';

import { CardStatus } from './models';

export const getSelectedPlan = (data: TFormData, plans: Plan[]) => {
  const rawData = data[SELECTED_PLAN_KEY];

  if (!rawData) {
    return { errorMessage: 'Plan ID is missing' };
  }
  const planId = rawData as string;
  const match = plans.find((plan) => plan.id === planId);

  if (!match) {
    return { planId, errorMessage: `Invalid plan ID "${planId}"` };
  }

  return { planId, plan: match };
};

export const transformPlans = (
  keyRoot: string,
  plans: Plan[],
  disabledPlanIds: (string | undefined)[] = []
): InputPlan[] => {
  return plans.map((plan) => ({
    key: `${keyRoot}-${plan.id}`,
    isDisabled: disabledPlanIds.includes(plan.id),
    type: FormInputTypes.PLAN,
    ...plan,
  }));
};

// TODO: improve binding context handling everywhere

export const translatePrice = (
  price: string,
  currency: Currency,
  duration: Duration
) => {
  // TODO: handle one-off purchases in the plugin and Plan interface properly, temporary workaround for Rural's use-case to avoid changing plugins
  // Only return the /{unit} suffix when the contract length is per-unit (per month, per year)
  if (duration.count === 1) {
    return t(`web.iap.price.periodical.${duration.unit.toLowerCase()}`, {
      purchase: { price, currency: translateCurrency(currency) },
    });
  }

  return t(`web.iap.price.oneOff`, {
    purchase: { price, currency: translateCurrency(currency) },
  });
};

export const translateDescription = (
  description: string,
  price: string,
  currency: Currency,
  date: Date
) => {
  return tDouble(description, {
    date,
    purchase: {
      price,
      currency: translateCurrency(currency),
    },
    // TODO: backward compatibility. remove later.
    price,
    currency: translateCurrency(currency),
  });
};

export const translateDiscount = (currency: Currency, voucher: Voucher) => {
  const {
    isPercent,
    discountAmount,
    duration: { count, unit },
  } = voucher;
  const discountKey = isPercent ? 'discountPercentage' : 'discountAbsolute';
  const unitKey = unit === DurationUnit.Forever ? 'forever' : `first${unit}`;

  return t(`web.iap.voucher.${discountKey}.${unitKey}`, {
    count,
    purchase: {
      voucher: {
        [discountKey]: discountAmount,
      },
      currency: isPercent ? '' : translateCurrency(currency),
    },
  });
};

export function isPromotionalSubscription(subscription?: Subscription) {
  return !!subscription?.customParameters?.['promotion'];
}

export const translateSubscriptionStatus = (subscription: Subscription) => {
  const { expiration, scheduledMove, nextPayment, nextPrice, status } =
    subscription;
  const isPromotionalSub = isPromotionalSubscription(subscription);

  if (
    !isPromotionalSub &&
    (status === SubscriptionStatus.PendingCancellation ||
      status === SubscriptionStatus.Canceled)
  ) {
    return t('iap.subscription.status.canceled', {
      date: expiration,
    });
  }

  if (status === SubscriptionStatus.Unknown) {
    return t('web.iap.subscription.status.unknown');
  }

  if (scheduledMove) {
    return tDouble('web.iap.subscription.status.moved', {
      date: scheduledMove.date,
      purchase: {
        next: {
          ...scheduledMove.nextPlan,
          currency: translateCurrency(scheduledMove.nextPlan.currency),
        },
      },
    });
  }

  if (isPromotionalSub && !scheduledMove) {
    return t('web.promos.subscription.status.cancelled', { date: nextPayment });
  }

  if (status === SubscriptionStatus.TimeLimited) {
    return t('web.iap.subscription.status.timeLimited', { date: expiration });
  }

  if (status === SubscriptionStatus.PendingActivation) {
    return t('web.iap.subscription.status.pendingActivation');
  }

  if (nextPrice === '0' && !nextPayment) {
    return t('web.iap.subscription.status.free');
  }

  return t('web.iap.subscription.status.recurring', { date: nextPayment });
};

export const translatePaymentMethodStatus = (
  status: CardStatus,
  card?: StoredCreditCard
) => {
  return t(
    status === CardStatus.Valid
      ? `iap.paymentMethod.card.type.debit`
      : `iap.paymentMethod.card.status.${status}`,
    {
      purchase: {
        ...(card && {
          card: {
            type: card.cardType,
            endingNumbers: card.cardNumber.slice(card.cardNumber.length - 4),
          },
        }),
      },
    }
  );
};

// Hard coded logic specifically for the MPP paywall.
// The plugin needs a sessionToken, but it can't be obtained from client side.
// We have a server endpoint to proxy this request to MPP.
// We rather not share that (and our server) with the plugin directly.
// So we'll just provide the plugin with a function to obtain the sessionToken.
export const fetchMppToken = async (): Promise<PaywallSession | null> => {
  return mppService
    .getToken()
    .then(({ data }) => {
      return {
        sessionToken: data.sessionId,
        accountReference: data.accountReference,
      };
    })
    .catch((err) => {
      console.error('MPP: authentication failed', err);

      return null;
    });
};

export const getPaywallSession = (sessionEndpoint: string) => {
  return axios.request<PaywallSession>({
    url: sessionEndpoint,
    method: 'GET',
  });
};

const translateCurrency = (currency: Currency) => {
  return t(`web.iap.currency.${currency?.toLowerCase()}`);
};

/**
 * This special function tries to deal with an interesting edge case in our translations.
 * For some reason on our DEV/PROD environments interpolation is working only for the first variable, when there
 * are two next to each other. This fix is just running t function twice, so that interpolation will work as
 * expected. Funny thing - it works fine on Storybook configuration and I did an investigation what are
 * the differences between those 2 configurations and found nothing :( Problematic texts comes from 3rd party endpoints
 * This function is expected to be removed once the root cause is found and resolved
 **/
function tDouble(text: string, params?: object) {
  return t(t(text, params), params);
}

/**
 * Take the `vendorTag` out of the URL, verify it based on the paywall config, and return it as a `CustomParameters` object.
 * @returns {CustomParameters|undefined} Object with `vendorTag` in it, or undefined.
 */
export const useSubscriptionParameters = (): CustomParameters | undefined => {
  const {
    features: { paywall = [] },
  } = useConfig();
  const location = useLocation();

  // disabled paywalls are alredy filtered out. if we have multiple enabled, use the first one.
  const supportedVendorTags = paywall[0]?.properties?.vendorTags || [];
  const vendorTag = new URLSearchParams(location.search).get('vendorTag') || '';

  if (supportedVendorTags.includes(vendorTag)) {
    return { vendorTag };
  }

  if (vendorTag) {
    console.debug('Ignored invalid vendorTag parameter', {
      configured: supportedVendorTags,
      actual: vendorTag,
    });
  }

  return undefined;
};

// Sometimes when the API ref is updating, ref.current can be null for a short while.
// I'm still not sure why it does that. Further investigation needed.
// In the meantime, I will retry on the next tick, maximum this many times.
const MAX_API_RETRIES = 5;

export const retryApi = async (
  apiRef: React.MutableRefObject<PaywallApi | undefined>,
  counter: number = 0,
  maxRetries: number = MAX_API_RETRIES
): Promise<PaywallApi> => {
  if (apiRef.current) {
    return apiRef.current;
  }

  if (counter > maxRetries) {
    throw new Error('API timeout');
  }

  return new Promise((resolve) => {
    window.setTimeout(
      () => resolve(retryApi(apiRef, counter + 1, maxRetries)),
      100
    );
  });
};

export const getPaywallApiFromRef = (
  apiRef: React.MutableRefObject<PaywallApi | undefined>
): PaywallApi => {
  // Common rejection when API method is not implemented
  const cantCall = () => Promise.reject("Can't call API");

  return {
    // `getProviderInfo` should be a mandatory method coming from the paywall plugins as
    // it would serve as the source of truth for the currently used provider
    getProviderInfo: () =>
      retryApi(apiRef).then(
        (api) => api.getProviderInfo?.() || { name: 'unknown' }
      ),

    getPlans: () =>
      retryApi(apiRef).then((api) => api.getPlans?.() || cantCall()),

    getCurrentSubscription: () =>
      retryApi(apiRef).then(
        (api) => api.getCurrentSubscription?.() || cantCall()
      ),

    getPaymentDetails: () =>
      retryApi(apiRef).then((api) => api.getPaymentDetails?.() || cantCall()),

    changePaymentMethod: (card: CreditCard) =>
      retryApi(apiRef).then(
        (api) => api.changePaymentMethod?.(card) || cantCall()
      ),
    updateRenewals: (isEnabled: boolean) =>
      retryApi(apiRef).then(
        (api) => api.updateRenewals?.(isEnabled) || cantCall()
      ),
    cancelSubscription: () =>
      retryApi(apiRef).then((api) => {
        if (api.cancelSubscription) {
          return api.cancelSubscription();
        }

        // TODO#paywall: Remove this once MPP has been updated to support the new name
        if (api.updateRenewals) {
          return api.updateRenewals(false);
        }

        return cantCall();
      }),
    restoreSubscription: () =>
      retryApi(apiRef).then((api) => {
        if (api.restoreSubscription) {
          return api.restoreSubscription();
        }

        // TODO#paywall: Remove this once MPP has been updated to support the new name
        if (api.updateRenewals) {
          return api.updateRenewals(true);
        }

        return cantCall();
      }),

    changePlan: (newPlanId: string) =>
      retryApi(apiRef).then((api) => {
        if (api.changePlan) {
          return api.changePlan(newPlanId);
        }

        // TODO#paywall: Remove this once MPP has been updated to support the new name
        if (api.moveSubscription) {
          return api.moveSubscription(newPlanId);
        }

        return cantCall();
      }),

    moveSubscription: (newPlanId: string) =>
      retryApi(apiRef).then(
        (api) => api.moveSubscription?.(newPlanId) || cantCall()
      ),
    redeemGift: (giftCode: string) =>
      retryApi(apiRef).then((api) => api.redeemGift?.(giftCode) || cantCall()),

    validateVoucher: (planId: string, voucherCode: string) =>
      retryApi(apiRef).then(
        (api) => api.validateVoucher?.(planId, voucherCode) || cantCall()
      ),

    purchase: (planId: string, options: PurchaseOptions) =>
      retryApi(apiRef).then(
        (api) => api.purchase?.(planId, options) || cantCall()
      ),
    supportedFeatures: () =>
      retryApi(apiRef).then((api) => api.supportedFeatures?.() || cantCall()),

    completePurchase: (paymentMethod: any) =>
      retryApi(apiRef).then(
        (api) => api.completePurchase?.(paymentMethod) || cantCall()
      ),
  };
};

export const fetchOauthToken = () =>
  userService
    .getOauthToken()
    .then((r) => r.data.oauth?.accessToken)
    .catch((err) => {
      console.error("Can't fetch OAuth token", err);

      return null;
    });

/**
 * Checks a list of features to see if they are supported.
 *
 * @returns A list of boolean values matching the support for each given feature
 */
export const supportsFeatures = (
  featuresToCheck: PaywallFeature[],
  supportedFeatures: PaywallFeature[]
): boolean[] =>
  featuresToCheck.map((feature) => supportedFeatures?.includes(feature));
